import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardReadSuccessOptions, useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { useToastWhen } from 'Src/feature/toasts/useToastWhen';

type UseCardReaderControlOptions = {
  onRead: (options: { read: CardReadSuccessOptions;  }) => void;
}

const sectionId = 'useCardReaderControl';
const useCardReaderControl = (options: UseCardReaderControlOptions) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const paymentFlow = usePaymentFlow();
  const currentCheck = useCurrentCheck();
  const ccPaymentMethods = usePaymentMethod.query.ccMethods();
  const cardReader = useCardReader({ id: sectionId });

  const [blocked, _blockedSet] = useState(false);

  const ref = useRef({
    read: undefined as undefined | CardReadSuccessOptions,
    initKey: '',
    buildInitKey: (options: { amount: number }) => `init-key-amount-${options.amount}`,
    blocked,
    reinit: (from: string) => console.info('reset on every render', { from }),
    dispose: cardReader.dispose,
  });

  const blockedSet = (next: boolean) => {
    ref.current.blocked = next;
    _blockedSet(ref.current.blocked);
  };

  // want to effect on every state change
  useEffect(() => {
    if (ref.current.blocked) {
      log.debug('init', 'blocked');
      return;
    }
    // Transaction dependencies missing: !check'
    if (!currentCheck.query.data) {
      log.debug('init', '!currentCheck');
      return;
    }

    if (currentCheck.query.data.status === 'Closed') {
      log.debug('init', 'currentCheck is closed');
      return;
    }

    // Transaction dependencies missing:!check.orderTypeId
    if (!currentCheck.query.data.orderTypeId) {
      log.debug('init', '!orderTypeId');
      return;
    }
    // Transaction dependencies missing: !ccPaymentMethods
    if (!ccPaymentMethods.data || ccPaymentMethods.data.length === 0) {
      log.debug('init', '!ccPaymentMethods');
      return;
    }

    // listen for any possible changes to the amount value
    let amount = 0;
    if (paymentFlow.is.part) amount = paymentFlow.part.amount ?? 0;
    else if (paymentFlow.is.split) amount = paymentFlow.split.amount ?? 0;
    else amount = currentCheck.query.data?.balance.unpaid ?? 0;

    if (!amount) {
      log.debug('init', '!amount');
      return;
    }

    const nextInitKey = ref.current.buildInitKey({ amount });
    // !!! only reinit if we have not already
    if (ref.current.initKey === nextInitKey) {
      log.debug('init', 'same init key');
      return;
    }
    ref.current.initKey = nextInitKey;

    const init = () => {
      cardReader.init({
        mode: 'emv',
        amount,
        onSuccess: async (read) => {
          ref.current.read = read;
          // after a read we want to reset the init key so another payment w/ that same amount can be initiated
          // - i.e. - if payment is split w/ exact amount
          ref.current.initKey = '';
          try {
            if (ref.current.blocked) return log.debug('onCardRead', '!!!', 'read is blocked', { amount });
            if (!read || !read.pax) return log.error('onCardRead', 'no pax data received', { amount });
            options.onRead({ read });
          } catch (error) {
            log.error('onCardRead', 'unknown error', { error, amount });
            ref.current.reinit('onCardRead.unknown.error');
          }
        },
        onTimeout: () => true,
        onError: () => true,
      });
    };

    // reset reinit just in case
    ref.current.reinit = (from: string) => {
      log.debug('reinit', 'from:', from ?? 'undefined');
      ref.current.initKey = '';
      init();
    };

    // call every chance we get as we should block all the time
    init();
  });

  // expose previous use cases
  const { is, state } = cardReader;

  const message = state.displayMessage?.toLowerCase() || '';

  // We're getting card reader statuses from the displayMessage, but PAX doesn't
  // supply the prompts for the D135, so we need to do some guesswork here.
  // PAX supplies the prompts for the D180, though, which are likely similar.
  useToastWhen({
    id: `${sectionId}-reader-timeout`,
    type: 'alert',
    props: {
      title: t('payment.info.pleaseWait'),
      severity: 'warning',
    },
  }, () => !blocked && state.card.isTimeout);

  useToastWhen({
    id: `${sectionId}-reader-fallback`,
    type: 'alert',
    props: {
      title: t('payment.info.chipReadUnsuccessfulPleaseSwipe'),
      severity: 'error',
    },
  }, () => message.includes('fallback'));

  useToastWhen({
    id: `${sectionId}-reader-reswipe`,
    type: 'alert',
    props: {
      title: t('payment.info.pleaseSwipeCard'),
      severity: 'error',
    },
  }, () => message.includes('reswipe'));

  useToastWhen({
    id: `${sectionId}-reader-tap-again`,
    type: 'alert',
    props: {
      title: t('payment.info.pleaseTapAgain'),
      severity: 'error',
    },
  }, () => message.includes('tap again'));

  useToastWhen({
    id: `${sectionId}-reader-remove-card`,
    type: 'alert',
    props: {
      title: t('payment.info.pleaseRemoveCard'),
      severity: 'warning',
    },
  }, () => message.includes('remove card'));

  useToastWhen({
    id: `${sectionId}-reader-invalid-card`,
    type: 'alert',
    props: {
      title: t('payment.info.invalidCard'),
      severity: 'error',
    },
  }, () => message.includes('invalid card'));

  useToastWhen({
    id: `${sectionId}-reader-see-phone`,
    type: 'alert',
    props: {
      title: t('payment.info.referToConsumerDevice'),
      severity: 'warning',
    },
  }, () => message.includes('see phone'));

  const { reinit } = ref.current;

  useEffect(() => {
    const { dispose } = ref.current;
    return () => {
      void dispose();
    };
  }, []);
  
  return {
    is,
    state,
    turn: {
      on: (from: string) => {
        log.debug('on', 'from:', from);
        blockedSet(false);
      },
      off: (from: string) => {
        log.debug('off', 'from:', from);
        blockedSet(true);
      },
    },
    set read(next: CardReadSuccessOptions | undefined) {
      ref.current.read = next;
    },
    get read() {
      return ref.current.read;
    },
    reinit,
  };
};

export { useCardReaderControl };
